'use client'

import { useTranslations } from 'next-intl'

import { TRoomDetail } from '@/entities/room'
import { useFormatCurrency, useMultiScrollState } from '@/shared/lib'
import { Notice } from '@/shared/ui'

import { TabValue } from '../../lib/tab'
import { Heading, Item, Label, List, Value, Discounts } from '../common'

type Props = Pick<
  TRoomDetail,
  | 'earlyCheckinDiscounts'
  | 'longTermDiscounts'
  | 'usingFee'
  | 'mgmtFee'
  | 'cleanFee'
  | 'deposit'
>

export const ContractInformation = ({
  earlyCheckinDiscounts,
  longTermDiscounts,
  usingFee,
  mgmtFee,
  cleanFee,
  deposit,
}: Props) => {
  const t = useTranslations()

  const { formatCurrency } = useFormatCurrency()

  const { registerScrollElement } = useMultiScrollState<HTMLElement>()

  return (
    <section
      className="bg-gray-0 flex gap-30 rounded-sm p-6"
      ref={element => {
        if (element) {
          registerScrollElement(TabValue.CONTRACT_INFORMATION, element)
        }
      }}
    >
      <Heading>{t('room_detail.lease_details.title')}</Heading>

      <div className="grow space-y-3">
        <div className="rounded-sm bg-white p-7.5">
          <List spacing="small" className="typo-body2">
            <Item className="flex-col justify-start gap-2.5">
              <div className="flex w-full items-center justify-between gap-2">
                <Label className="font-semibold">
                  {t('room_detail.lease_details.label.rent')}
                </Label>
                <Value className="font-medium">
                  {formatCurrency(usingFee)} / {t('common.week_unit')}
                </Value>
              </div>
              <Discounts
                earlyCheckinDiscounts={earlyCheckinDiscounts}
                longTermDiscounts={longTermDiscounts}
                highlightColor="gray"
                className="w-full"
              />
            </Item>
            <Item className="justify-between">
              <Label className="font-semibold">
                {t('room_detail.lease_details.label.utility_fee')}
              </Label>
              <Value className="font-medium">
                {formatCurrency(mgmtFee)} / {t('common.week_unit')}
              </Value>
            </Item>
            <Item className="justify-between">
              <Label className="font-semibold">
                {t('room_detail.lease_details.label.cleaning_fee')}
              </Label>
              <Value className="font-medium">{formatCurrency(cleanFee)}</Value>
            </Item>
            <Item className="justify-between">
              <Label className="font-semibold">
                {t('room_detail.lease_details.label.deposit')}
              </Label>
              <Value className="font-medium">{formatCurrency(deposit)}</Value>
            </Item>
          </List>
        </div>

        <Notice.Title className="font-normal">
          {t('room_detail.lease_details.notice')}
        </Notice.Title>
      </div>
    </section>
  )
}
